import {
  GroupChannelListOrder,
  MyMemberStateFilter,
  type UnreadChannelFilter,
} from "@sendbird/chat/groupChannel";
import { type SdkStore } from "@sendbird/uikit-react/types/lib/types";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { isExpectedSendbirdNetworkError } from "./utils/isExpectedSendbirdNetworkError";
import { type Dispatch } from "redux";
import { setChannels } from "src/modules/chat";

const CHAT_CHANNEL_QUERY_LIMIT = 100;

export type CustomTypesChannelFilter = "flex" | "placements" | "all" | "starred";

export interface FetchChannelsProps {
  sdk: SdkStore["sdk"];
  dispatch: Dispatch;
  nicknameContainsFilter?: string;
  customTypesFilter?: CustomTypesChannelFilter;
  unreadChannelsOnly?: UnreadChannelFilter;
}

export async function fetchChannels(props: FetchChannelsProps) {
  const { sdk, dispatch, nicknameContainsFilter, customTypesFilter, unreadChannelsOnly } = props;

  if (!sdk?.groupChannel) {
    return setChannels(dispatch, []);
  }

  let customTypesFilterArray: string[] | undefined;
  if (customTypesFilter && customTypesFilter !== "all") {
    switch (customTypesFilter) {
      case "flex":
        customTypesFilterArray = [""];
        break;
      case "placements":
        customTypesFilterArray = ["placements"];
        break;
    }
  }
  const query = sdk.groupChannel.createMyGroupChannelListQuery({
    includeEmpty: true,
    unreadChannelFilter: unreadChannelsOnly,
    order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
    limit: CHAT_CHANNEL_QUERY_LIMIT,
    myMemberStateFilter: MyMemberStateFilter.JOINED,
    ...(isDefined(customTypesFilterArray) && { customTypesFilter: customTypesFilterArray }),
    ...(isDefined(nicknameContainsFilter) && { nicknameContainsFilter }),
  });

  try {
    const list = await query.next();
    void setChannels(dispatch, list);
  } catch (error) {
    // This fetch runs app-wide in the background as the Sendbird SDK reconnects
    // (tab focus changes, flaky networks) and is invoked fire-and-forget, so an
    // uncaught rejection here surfaced as unhandled Datadog RUM Error Tracking
    // noise (SendbirdError: There was a network error., issue
    // f6292cdc-d16a-11ee-af53-da7ad0900002). Expected transient connectivity
    // failures resolve on their own, so swallow them and keep the existing
    // channel list. Keep unexpected failures observable.
    if (isExpectedSendbirdNetworkError(error)) {
      return;
    }

    logError(APP_V2_APP_EVENTS.FETCH_CHAT_CHANNELS_FAILURE, { error });
  }
}
